import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailCashShiftOpens, type CashShiftOpen, type ShiftStatus } from '@/api/retailCashShiftOpen';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.shift.open';

const STATUS_LABEL: Record<string, string> = {
  open: 'Відкрита',
  closed: 'Закрита',
  voided: 'Анульована',
};

export default function CashShiftOpensPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-cash-shift-open', orgId, statusFilter],
    queryFn: () =>
      retailCashShiftOpens.list({
        organization_id: orgId || undefined,
        shift_status: statusFilter ? (statusFilter as ShiftStatus) : undefined,
        limit: 500,
      }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<CashShiftOpen>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата/час відкриття',
        width: 180,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'pos_workstation_id',
        header: 'POS',
        sortValue: (r) => r.pos_workstation_id ?? '',
        cell: (r) => r.pos_workstation_id,
      },
      {
        key: 'opening_cash_amount',
        header: 'Розмін, грн',
        width: 150,
        align: 'right',
        sortValue: (r) => Number(r.opening_cash_amount),
        cell: (r) =>
          r.opening_cash_amount
            ? Number(r.opening_cash_amount).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '',
      },
      {
        key: 'shift_status',
        header: 'Статус зміни',
        width: 150,
        sortValue: (r) => STATUS_LABEL[r.shift_status] ?? r.shift_status,
        cell: (r) => STATUS_LABEL[r.shift_status] ?? r.shift_status,
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав відкривати зміну (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Відкриття зміни
          <HintBadge
            title="Що це?"
            text={
              'Документ-журнал відкриття касирської зміни. ' +
              'Зміна — це період часу, протягом якого касир обслуговує клієнтів на одній POS-станції.\n\n' +
              'Що фіксується:\n' +
              '• Дата та час відкриття.\n' +
              '• POS-станція + касир (автоматично з контексту).\n' +
              '• Розмін у касі (готівка, яка вже є на момент відкриття).\n\n' +
              'Правила:\n' +
              '• На одній POS-станції може бути ТIЛЬКИ ОДНА відкрита зміна одночасно.\n' +
              '• Щоб закрити зміну — використовуйте «Закриття зміни (Z-звіт)».\n\n' +
              'Касир відкриває зміну при першому вході у /pos. Через цей бух-UI ' +
              'можна редагувати чернетки або переглядати історію.'
            }
          />
        </h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Всі статуси</option>
          <option value="open">Відкрита</option>
          <option value="closed">Закрита</option>
          <option value="voided">Анульована</option>
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/cash-shift-open/new')}
          style={{ marginLeft: 'auto' }}
        >
          + Відкриття зміни
        </button>
      </div>
      <DataTable<CashShiftOpen>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/retail/cash-shift-open/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
